import { getLocalStorage, getSessionStorage } from './storage.js';
import { CustomEvent } from './custom-event.js';
const APP_EVENT_PREFIX = 'app_event_'; // app事件前缀
const isClient = typeof window !== 'undefined';
const LATEST_APP_EVENT = 'latest_app_event';
let _nodeEventEmitter = null;
let broadcastInited = false; // 初始化事件广播
function getNodeEventEmitter() {
    if (!_nodeEventEmitter) {
        const events = require('events');
        _nodeEventEmitter = new events.EventEmitter();
    }
    return _nodeEventEmitter;
}
/**
 * 从 session 中获取并触发最近的一次 app event
 */
function dispatchLatestAppEventFromSession() {
    const session = getSessionStorage();
    if (session && session.storageAvailable) {
        let data = session.getItem(LATEST_APP_EVENT);
        if (data) {
            session.removeItem(LATEST_APP_EVENT);
            data = JSON.parse(data);
            dispatchWindowEvent(data.event, data.params);
        }
    }
}
/**
 * 派发事件，兼容服务端和客户端
 * 在服务端使用 EventEmitter，在客户端使用 window
 * @param event 事件名，统一用驼峰命名
 * @param params 事件参数
 */
function dispatchEvent(event, params = {}) {
    if (!isClient) {
        const nodeEventEmitter = getNodeEventEmitter();
        const evt = new CustomEvent(event, {
            detail: params,
            cancelable: false
        });
        nodeEventEmitter.emit(event, evt);
    }
    else {
        dispatchWindowEvent(event, params);
    }
}
/**
 * 监听事件，兼容服务端和客户端
 * 在服务端使用 EventEmitter，在客户端使用 window
 * @param event
 * @param listener 为了和客户端保持统一，事件回调都只有一个 CustomEvent 对象参数，CustomEvent.detail 为触发的事件参数
 */
function addEventListener(event, listener) {
    if (!isClient) {
        const nodeEventEmitter = getNodeEventEmitter();
        nodeEventEmitter.addListener(event, listener);
    }
    else {
        if (!broadcastInited) {
            broadcastInited = true;
            /**
             * 转发app自定义事件
             */
            window.addEventListener('storage', (e) => {
                const key = e.key;
                const value = e.newValue;
                if (key.indexOf(APP_EVENT_PREFIX) === 0 && value) {
                    const event = key.replace(APP_EVENT_PREFIX, '');
                    try {
                        // 通过自定义事件再分发
                        const evt = new CustomEvent(event, {
                            detail: JSON.parse(e.newValue)
                        });
                        evt && window.dispatchEvent(evt);
                    }
                    catch (err) { }
                }
            });
        }
        window.addEventListener(event, listener);
    }
}
/**
 * 移除事件监听，兼容服务端和客户端
 * 在服务端使用 EventEmitter，在客户端使用 window
 * @param event
 * @param listener
 */
function removeEventListener(event, listener) {
    if (!isClient) {
        const nodeEventEmitter = getNodeEventEmitter();
        nodeEventEmitter.removeListener(event, listener);
    }
    else {
        window.removeEventListener(event, listener);
    }
}
/**
 * 派发window事件，该方法用于将自定义事件通知到当前window
 * @param event 事件名，统一用驼峰命名
 * @param params 事件参数
 */
function dispatchWindowEvent(event, params = {}) {
    if (!isClient) {
        return;
    }
    const evt = new CustomEvent(event, {
        detail: params
    });
    evt && window.dispatchEvent(evt);
}
/**
 * app事件目的地
 */
export var AppEventTarget;
(function (AppEventTarget) {
    AppEventTarget[AppEventTarget["OtherWebView"] = 1] = "OtherWebView";
    AppEventTarget[AppEventTarget["Native"] = 2] = "Native";
    AppEventTarget[AppEventTarget["Current"] = 4] = "Current"; // 当前webView 或者当前 node进程
})(AppEventTarget = AppEventTarget || (AppEventTarget = {}));
/**
 * 派发app事件
 * @description 该方法用于将特定事件通知到多个webView和native
 * @param event 事件名，统一用驼峰命名
 * @param params 事件参数
 * @param target 目的地，默认只通知其他webView
 */
function dispatchAppEvent(event, params = {}, target = AppEventTarget.OtherWebView) {
    if (!isClient) {
        return;
    }
    if (target & AppEventTarget.Current) {
        dispatchWindowEvent(event, params);
    }
    if (target & AppEventTarget.OtherWebView) {
        // 使用 localStorage 通信
        if (getLocalStorage().storageAvailable) {
            const key = `${APP_EVENT_PREFIX}${event}`;
            window.localStorage.setItem(key, JSON.stringify(params));
            setTimeout(() => {
                window.localStorage.removeItem(key);
            }, 50);
        }
        // 如果支持 sessionStorage，缓存最后的一次事件，用于单个 webView 导航堆栈里多个页面的通信
        const session = getSessionStorage();
        if (session.storageAvailable) {
            session.setItem(LATEST_APP_EVENT, JSON.stringify({
                event,
                params
            }));
        }
    }
    if (target & AppEventTarget.Native) {
        // todo，将消息转发到客户端
    }
}
export { dispatchWindowEvent, dispatchAppEvent, dispatchEvent, addEventListener, removeEventListener, dispatchLatestAppEventFromSession, CustomEvent };
